import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { TrendingUp, Percent, AlertCircle, RefreshCw } from 'lucide-react';
import { effectiveElo, hostBonus } from '../../lib/sim/match';
import { lambdaFor } from '../../lib/sim/goals';

function poissonPMF(k, lambda) {
  let logP = -lambda + k * Math.log(lambda);
  for (let i = 2; i <= k; i++) logP -= Math.log(i);
  return Math.exp(logP);
}

function calculateProbabilities(a, b) {
  const eloA = effectiveElo(a, 'group');
  const eloB = effectiveElo(b, 'group');
  const bonusA = hostBonus(a, false);
  const bonusB = hostBonus(b, false);
  
  const lambdaA = lambdaFor(eloA, eloB, bonusA);
  const lambdaB = lambdaFor(eloB, eloA, bonusB);
  
  const MAX_GOALS = 8;
  const pH = new Array(MAX_GOALS + 1);
  const pA = new Array(MAX_GOALS + 1);
  for (let k = 0; k <= MAX_GOALS; k++) {
    pH[k] = poissonPMF(k, lambdaA);
    pA[k] = poissonPMF(k, lambdaB);
  }
  
  let pHome = 0, pDraw = 0, pAway = 0;
  for (let h = 0; h <= MAX_GOALS; h++) {
    for (let a = 0; a <= MAX_GOALS; a++) {
      const joint = pH[h] * pA[a];
      if (h > a) pHome += joint;
      else if (h === a) pDraw += joint;
      else pAway += joint;
    }
  }
  const norm = pHome + pDraw + pAway;
  return {
    home: pHome / norm,
    draw: pDraw / norm,
    away: pAway / norm
  };
}

// Map common names from the odds API to the simulator's database names
const NAME_MAP = {
  'Rep. tchèque': 'République Tchèque',
  'Rép. tchèque': 'République Tchèque',
  'RD Congo': 'Congo DR',
  'Equateur': 'Équateur',
  'Curaçao': 'Curacao',
  'Afrique du Sud': 'South Africa',
  'Corée du Sud': 'South Korea',
  'Nouvelle-Zélande': 'New Zealand',
  'Arabie saoudite': 'Saudi Arabia',
  'Arabie Saoudite': 'Saudi Arabia',
  'Belgique': 'Belgium',
  'Allemagne': 'Germany',
  'Espagne': 'Spain',
  'Italie': 'Italy',
  'Angleterre': 'England',
  'Pays-Bas': 'Netherlands',
  'Maroc': 'Morocco',
  'Brésil': 'Brazil',
  'Mexique': 'Mexico',
  'Algérie': 'Algeria',
  'Autriche': 'Austria',
  'Suède': 'Sweden',
  'Égypte': 'Egypt',
  'Croatie': 'Croatia',
  'Tunisie': 'Tunisia',
  'Norvège': 'Norway',
  'Suisse': 'Switzerland',
  'Japon': 'Japan'
};

function normalizeName(name) {
  if (!name) return '';
  const mapped = NAME_MAP[name];
  if (mapped) return mapped.toLowerCase();
  return name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function ValueBetsFinder({ result }) {
  const [odds, setOdds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/odds`)
      .then(({ data }) => {
        if (!cancelled) {
          setOdds(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Erreur de chargement des cotes.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const valueBets = useMemo(() => {
    if (odds.length === 0 || !result) return [];
    
    // Filter for World Cup matches
    const wcOdds = odds.filter(m => m.competition === 'Coupe du Monde');
    if (wcOdds.length === 0) return [];

    const teamLookup = new Map();
    result.teams.forEach(t => {
      teamLookup.set(normalizeName(t.name), t);
      if (t.name_fr) teamLookup.set(normalizeName(t.name_fr), t);
      if (t.name_en) teamLookup.set(normalizeName(t.name_en), t);
    });

    const betsList = [];

    wcOdds.forEach(match => {
      const homeTeam = teamLookup.get(normalizeName(match.home));
      const awayTeam = teamLookup.get(normalizeName(match.away));
      
      if (!homeTeam || !awayTeam) return;

      // Compute simulated probabilities
      const prob = calculateProbabilities(homeTeam, awayTeam);

      // Odds comparisons
      // Home Value
      if (match.best_home > 1) {
        const implied = 1 / match.best_home;
        const edge = prob.home - implied;
        if (edge > 0.02) { // 2% edge threshold
          betsList.push({
            matchId: match.match_id,
            home: match.home,
            away: match.away,
            selection: match.home,
            type: '1',
            cote: match.best_home,
            probSim: prob.home,
            probImplied: implied,
            edge
          });
        }
      }

      // Draw Value
      if (match.best_draw > 1) {
        const implied = 1 / match.best_draw;
        const edge = prob.draw - implied;
        if (edge > 0.02) {
          betsList.push({
            matchId: match.match_id,
            home: match.home,
            away: match.away,
            selection: 'Match Nul',
            type: 'N',
            cote: match.best_draw,
            probSim: prob.draw,
            probImplied: implied,
            edge
          });
        }
      }

      // Away Value
      if (match.best_away > 1) {
        const implied = 1 / match.best_away;
        const edge = prob.away - implied;
        if (edge > 0.02) {
          betsList.push({
            matchId: match.match_id,
            home: match.home,
            away: match.away,
            selection: match.away,
            type: '2',
            cote: match.best_away,
            probSim: prob.away,
            probImplied: implied,
            edge
          });
        }
      }
    });

    // Sort by edge descending
    return betsList.sort((a, b) => b.edge - a.edge);
  }, [odds, result]);

  if (loading) {
    return (
      <div className="card-elev p-10 text-center text-gray-400 text-xs flex items-center justify-center gap-2 border border-[#2A4A6B]">
        <RefreshCw size={14} className="animate-spin text-[#00C853]" />
        Calcul des opportunités de paris (Value Bets) en direct...
      </div>
    );
  }

  if (error || valueBets.length === 0) {
    return (
      <div className="card-elev p-6 text-center text-gray-400 text-xs border border-[#2A4A6B]">
        Aucun Value Bet détecté pour le moment sur la Coupe du Monde.
      </div>
    );
  }

  return (
    <div className="card-elev p-5 sm:p-6 mb-8 border border-[#2A4A6B]">
      <header className="mb-4 flex items-center gap-2">
        <TrendingUp className="text-[#00C853]" size={20} />
        <div>
          <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
            Détecteur de Value Bets
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Sélections où le modèle estime que la cote du bookmaker est sous-évaluée.
          </p>
        </div>
      </header>

      <div className="overflow-x-auto rounded-xl border border-[#2A4A6B]/50 bg-[#1E3A5F]/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#2A4A6B]/50 bg-[#1E3A5F]/20 font-mono text-[10px] uppercase tracking-wider text-gray-400">
              <th className="px-4 py-3 text-left">Match</th>
              <th className="px-4 py-3 text-left">Pari conseillé</th>
              <th className="px-4 py-3 text-center">Meilleure Cote</th>
              <th className="px-4 py-3 text-right">Probabilité Sim.</th>
              <th className="px-4 py-3 text-right">Avantage (Edge)</th>
            </tr>
          </thead>
          <tbody>
            {valueBets.map((b, idx) => (
              <tr
                key={`${b.matchId}-${b.type}-${idx}`}
                className="border-b border-[#2A4A6B]/30 hover:bg-[#1E3A5F]/20 transition-colors"
              >
                <td className="px-4 py-3 text-xs sm:text-sm font-semibold text-white">
                  {b.home} <span className="text-gray-500 font-normal">vs</span> {b.away}
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="px-2 py-0.5 rounded bg-[#00C853]/15 text-[#00C853] font-bold">
                    {b.selection}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="px-2 py-1 rounded bg-[#00C853] text-black font-bold font-mono text-xs">
                    {b.cote.toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-white">
                  {(b.probSim * 100).toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs tabular-nums font-bold text-[#00C853]">
                  +{(b.edge * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-start gap-1.5 text-[10px] text-gray-500">
        <AlertCircle size={12} className="shrink-0 mt-0.5" />
        <span>
          L'avantage (edge) correspond à la différence de probabilités entre notre modèle statistique et celle estimée par la meilleure cote disponible sur le marché. Plus l'avantage est élevé, plus le pari est statistiquement rentable.
        </span>
      </div>
    </div>
  );
}
